import { logOperationalEvent, logWarn } from 'saddlebag-logger';
import observer from 'saddlebag-observer';

import { getConsumer, getTCFStorageKey, isKetchMode } from './cmp-state';
import {
  COMPONENT,
  KETCH_API_TIMEOUT,
  KETCH_SDK_NOT_LOADED_ERROR,
  TCF_DATA_PARSE_FAILURE,
  ketchModeRequiredError,
} from './constants/events';
import { isBannerVisible } from './ketch-event-bridge';
import { DEFAULT_KETCH_API_TIMEOUT_MS } from './ketch-fallback';
import {
  enrichVendorsWithTCFMetadata,
  extractTCFItemsFromPurposes,
  getWindow,
} from './ketch-helpers';
import { KETCH_PURPOSE_CODES } from './ketch-types';

import type {
  ConsentAction,
  ConsentConfig,
  KetchPurposes,
  KetchTCFData,
  RawKetchFullConfig,
  VendorConsents,
} from './ketch-types';

// Upper bound on how long updateConsent waits for Ketch to finish writing
// the TCF string and `_swb_consent_` cookie after the Ketch SDK call returns.
// Ketch's acceptAllConsent/rejectAllConsent/setConsent trigger an async
// persistence step; without this barrier, callers that navigate or reload
// right after updateConsent resolves race the write and see stale state
// (banner resurfaces on return visit because TCF string is empty).
const KETCH_PERSIST_TIMEOUT_MS = 1500;

const TRACKING_PREFERENCES_EVENT = 'tracking-preferences-changed';
// Compatibility bridge: the legacy Skyscanner CMP publishes
// 'cookie-policy-accepted' from global-components/cookie-banner ONLY on
// the Accept All button (CookieBanner.tsx:430). Reject ("Essential
// Only") and Manage Settings paths do NOT publish it. Some downstream
// consumers (e.g. strevda-runtime's Bootstrap.setupServices) gate work
// on this event. In Ketch mode the legacy banner is not rendered, so
// we mirror the legacy contract from the 'accept_all' case below — and
// only that case — so dual-running CMPs keep a uniform observable
// contract. Long-term, consumers should migrate to
// TRACKING_PREFERENCES_EVENT (the canonical event) and gate on
// getPreferences()[ADVERTS] === true.
const COOKIE_POLICY_ACCEPTED_EVENT = 'cookie-policy-accepted';

// saddlebag-observer replays queued publishes to every new subscriber
// (dispatched async via setTimeout(0)). For a second consent update on the
// same page (e.g. multiple saves in Privacy Centre), the subscription opened
// below would otherwise fire on the previous save's replayed publish and
// resolve immediately — bypassing the persistence barrier. Snapshot the
// queue length before subscribing so those replays can be counted and
// ignored; only the fresh publish that follows Ketch's new write counts.
function getQueuedPublishCount(): number {
  const win = getWindow() as unknown as {
    Sb?: { ObserverMemory?: { queued?: Record<string, unknown[]> } };
  };
  const queue = win.Sb?.ObserverMemory?.queued?.[TRACKING_PREFERENCES_EVENT];
  return Array.isArray(queue) ? queue.length : 0;
}

// Module-level cache — Ketch config is static per page load
let fullConfigPromise: Promise<Record<string, unknown>> | null = null;

/**
 * Returns a cached Promise for the Ketch full config.
 * First call triggers ketch('getFullConfig'); subsequent calls reuse the same promise.
 * On timeout, cache is cleared so a retry is possible.
 * @returns Cached promise resolving to the Ketch full config object
 */
function getCachedFullConfig(): Promise<Record<string, unknown>> {
  const browserWindow = getWindow();
  if (typeof browserWindow.ketch !== 'function') {
    return Promise.reject(new Error(KETCH_SDK_NOT_LOADED_ERROR));
  }

  if (fullConfigPromise) {
    return fullConfigPromise;
  }

  const { ketch } = browserWindow;

  fullConfigPromise = new Promise((resolve) => {
    const timeoutId = setTimeout(() => {
      logOperationalEvent({
        eventName: KETCH_API_TIMEOUT,
        component: COMPONENT,
        consumer: getConsumer(),
        apiName: 'getFullConfig',
      });
      logWarn({
        component: COMPONENT,
        message: `${KETCH_API_TIMEOUT}: getFullConfig timed out after ${DEFAULT_KETCH_API_TIMEOUT_MS}ms`,
      });
      fullConfigPromise = null; // Clear cache so retry is possible
      resolve({});
    }, DEFAULT_KETCH_API_TIMEOUT_MS);

    ketch('getFullConfig', (config: unknown) => {
      clearTimeout(timeoutId);
      resolve(config as Record<string, unknown>);
    });
  });

  return fullConfigPromise;
}

/** @internal Reset cached config — for testing only */
export function resetFullConfigCache(): void {
  fullConfigPromise = null;
}

/**
 * Consent domain invariant: when advertising is OFF, all TCF-dependent
 * consents must also be OFF. Prevents stale caller state from reaching Ketch.
 *
 * @param purposes - Ketch purpose codes with boolean consent values
 * @param vendorConsents - Vendor consent map with granted/denied status
 * @returns Corrected purposes and vendorConsents
 */
function enforceAdvertisingTCFAlignment(
  purposes: KetchPurposes | undefined,
  vendorConsents: VendorConsents | undefined,
): {
  purposes: KetchPurposes | undefined;
  vendorConsents: VendorConsents | undefined;
} {
  if (!purposes || purposes[KETCH_PURPOSE_CODES.ADVERTS] !== false) {
    return { purposes, vendorConsents };
  }

  const corrected: KetchPurposes = { ...purposes };
  for (const key of Object.keys(corrected)) {
    if (
      key.startsWith('tcf_purpose_') ||
      key.startsWith('tcf_specialFeature_')
    ) {
      corrected[key] = false;
    }
  }

  let correctedVendors = vendorConsents;
  if (vendorConsents) {
    correctedVendors = { ...vendorConsents };
    if (correctedVendors.tcf) {
      correctedVendors.tcf = Object.fromEntries(
        Object.keys(correctedVendors.tcf).map((id) => [id, 'denied' as const]),
      );
    }
    if (correctedVendors.google) {
      correctedVendors.google = Object.fromEntries(
        Object.keys(correctedVendors.google).map((id) => [
          id,
          'denied' as const,
        ]),
      );
    }
  }

  return { purposes: corrected, vendorConsents: correctedVendors };
}

/**
 * Updates consent via Ketch SDK.
 *
 * Behaviour:
 * - In 'ketch' mode: Delegates to appropriate Ketch function, then waits for
 *   Ketch to persist consent to storage (TCF localStorage + `_swb_consent_`
 *   cookie). Once persistence is signalled (via the
 *   `tracking-preferences-changed` observer event from the Ketch event
 *   bridge), emits Ketch's `hideExperience` event so the IAB TCF lifecycle
 *   transitions out of `cmpuishown` to `useractioncomplete`. Without this,
 *   the host's custom banner UI never tells Ketch the experience has closed,
 *   GAM/Prebid see a stale `cmpuishown` signal, and personalised ads do not
 *   render. The same emit also runs on the persistence-timeout fallback so
 *   the lifecycle still completes if the event bridge is not set up.
 *   Also re-publishes the legacy `cookie-policy-accepted` observer event
 *   for the `accept_all` action only (matching the legacy banner's
 *   accept-only behaviour) so downstream consumers still subscribed to
 *   that event (e.g. strevda-runtime) work uniformly across legacy and
 *   Ketch CMPs while both are running.
 * - In 'skyscanner' mode: Fails silently with warning log
 *
 * @param params - Consent action to perform
 * @returns Promise that resolves when consent is updated AND persisted
 * @throws Error if Ketch SDK not loaded (in ketch mode only)
 */
export function updateConsent(params: ConsentAction): Promise<void> {
  if (!isKetchMode()) {
    logWarn({
      message: 'updateConsent called in skyscanner mode - no-op',
      component: COMPONENT,
    });
    return Promise.resolve();
  }

  const browserWindow = getWindow();
  if (typeof browserWindow.ketch !== 'function') {
    return Promise.reject(new Error(KETCH_SDK_NOT_LOADED_ERROR));
  }

  const { ketch } = browserWindow;

  return new Promise((resolve, reject) => {
    let settled = false;
    const queuedReplays = getQueuedPublishCount();
    let replaysSeen = 0;
    const subscription = observer.subscribe(TRACKING_PREFERENCES_EVENT, () => {
      if (replaysSeen < queuedReplays) {
        replaysSeen += 1;
        return;
      }
      if (settled) return;
      settled = true;
      observer.unsubscribe(subscription);
      clearTimeout(timeoutId);
      // try/finally so a synchronous throw from ketch('emit', ...) does not
      // leave the promise permanently pending. Errors still propagate to
      // observability via the publisher's own handler; we only guarantee
      // resolve() runs.
      try {
        ketch('emit', 'hideExperience');
      } finally {
        resolve();
      }
    });
    const timeoutId = setTimeout(() => {
      if (settled) return;
      settled = true;
      observer.unsubscribe(subscription);
      logOperationalEvent({
        eventName: KETCH_API_TIMEOUT,
        component: COMPONENT,
        consumer: getConsumer(),
        apiName: 'updateConsent',
      });
      logWarn({
        component: COMPONENT,
        message: `${KETCH_API_TIMEOUT}: updateConsent persistence timed out after ${KETCH_PERSIST_TIMEOUT_MS}ms`,
      });
      try {
        ketch('emit', 'hideExperience');
      } finally {
        resolve();
      }
    }, KETCH_PERSIST_TIMEOUT_MS);

    try {
      switch (params.action) {
        case 'accept_all':
          ketch('acceptAllConsent', false, true);
          observer.publish(COOKIE_POLICY_ACCEPTED_EVENT);
          break;

        case 'reject_all':
          ketch('rejectAllConsent', false, true);
          break;

        case 'set': {
          const aligned = enforceAdvertisingTCFAlignment(
            params.purposes,
            params.vendorConsents,
          );
          ketch(
            'setConsent',
            {
              purposes: aligned.purposes,
              vendorConsents: aligned.vendorConsents,
            },
            'userUpdate',
          );
          break;
        }

        default:
          // TypeScript exhaustiveness check — unknown action, resolve immediately
          settled = true;
          observer.unsubscribe(subscription);
          clearTimeout(timeoutId);
          resolve();
      }
    } catch (err) {
      if (settled) return;
      settled = true;
      observer.unsubscribe(subscription);
      clearTimeout(timeoutId);
      reject(err instanceof Error ? err : new Error(String(err)));
    }
  });
}

/**
 * Gets consent configuration from Ketch SDK for Privacy Centre UI.
 *
 * @returns Promise with consent configuration
 * @throws Error if not in Ketch CMP mode
 * @throws Error if Ketch SDK not loaded
 */
export async function getConsentConfig(): Promise<ConsentConfig> {
  if (!isKetchMode()) {
    throw new Error(ketchModeRequiredError('getConsentConfig'));
  }

  const raw = (await getCachedFullConfig()) as unknown as RawKetchFullConfig;
  const allPurposes = raw.purposes ?? [];
  const { services } = raw;
  const tcfVendors = raw.systems?.tcf ?? [];

  // Extract features, special features, special purposes from the expanded
  // purposes array (Ketch now includes these as tcf_feature_*, tcf_specialPurpose_*, etc.)
  const tcfItems = extractTCFItemsFromPurposes(allPurposes);

  // Enrich vendors with TCF metadata from systems.tcf[].tcfMetadata
  const enrichment = enrichVendorsWithTCFMetadata(raw.vendors, tcfVendors);

  // Purpose-level extraction is the primary source (has descriptions from Ketch config).
  // Fall back to vendor-deduplicated data if purposes don't contain TCF items.
  const features =
    tcfItems.features.length > 0 ? tcfItems.features : enrichment.features;
  const specialFeatures =
    tcfItems.specialFeatures.length > 0
      ? tcfItems.specialFeatures
      : enrichment.specialFeatures;
  const specialPurposes =
    tcfItems.specialPurposes.length > 0
      ? tcfItems.specialPurposes
      : enrichment.specialPurposes;

  // Separate Ketch purposes (top-level toggles) from TCF purposes (detailed breakdown).
  // TCF items are also exposed via dedicated features/specialFeatures/specialPurposes arrays.
  const ketchPurposes = allPurposes.filter(
    (p) => p.code && !p.code.startsWith('tcf_'),
  );
  const tcfPurposes = allPurposes.filter((p) =>
    p.code?.startsWith('tcf_purpose_'),
  );

  const mapPurpose = (p: (typeof allPurposes)[0]) => ({
    code: p.code ?? '',
    name: p.name ?? '',
    description: p.description,
    legalBasisCode: p.legalBasisCode ?? '',
    illustrations: p.illustrations,
    systems: p.systems,
  });

  return {
    purposes: ketchPurposes.map(mapPurpose),
    tcfPurposes:
      tcfPurposes.length > 0 ? tcfPurposes.map(mapPurpose) : undefined,
    jurisdiction: raw.jurisdiction || '',
    vendors: enrichment.vendors,
    services,
    features,
    specialFeatures,
    specialPurposes,
  };
}

/**
 * Gets the IAB TCF TC String from localStorage.
 *
 * The TC String is stored by Ketch in localStorage under the key
 * `_ketch_tcf_tcdata_v1_` as a base64 encoded JSON object containing
 * `tcString` field.
 *
 * @returns TC String or null if not available
 * @throws Error if not in Ketch CMP mode
 */
export function getTCString(): string | null {
  if (!isKetchMode()) {
    throw new Error(ketchModeRequiredError('getTCString'));
  }

  const browserWindow = getWindow();
  if (!('localStorage' in browserWindow)) {
    return null;
  }

  try {
    const storedValue = browserWindow.localStorage?.getItem(getTCFStorageKey());
    if (!storedValue) {
      return null;
    }

    // Decode base64 and parse JSON
    const decoded = atob(storedValue);
    const data = JSON.parse(decoded) as KetchTCFData;

    return data.tcString ?? null;
  } catch {
    // If decoding or parsing fails, return null
    logOperationalEvent({
      eventName: TCF_DATA_PARSE_FAILURE,
      component: COMPONENT,
      consumer: getConsumer(),
    });
    logWarn({
      component: COMPONENT,
      message: `${TCF_DATA_PARSE_FAILURE}: Failed to parse TCF data from localStorage`,
    });
    return null;
  }
}

/**
 * Returns whether the consent banner is currently being shown.
 *
 * This is tracked via Ketch's willShowExperience and hideExperience events.
 * The event bridge must be set up (via setupKetchEventBridge) for this to work.
 *
 * @returns true if banner is currently visible, false otherwise
 * @throws Error if not in Ketch CMP mode
 */
export function isConsentBannerBeingShown(): boolean {
  if (!isKetchMode()) {
    throw new Error(ketchModeRequiredError('isConsentBannerBeingShown'));
  }

  return isBannerVisible();
}

/**
 * Gets the count of vendors from Ketch SDK configuration.
 *
 * This returns the total number of vendors configured in Ketch,
 * useful for displaying vendor count in consent banner UI.
 *
 * @returns Promise with vendor count, or 0 if unavailable
 * @throws Error if not in Ketch CMP mode
 * @throws Error if Ketch SDK not loaded
 */
export async function getVendorCount(): Promise<number> {
  if (!isKetchMode()) {
    throw new Error(ketchModeRequiredError('getVendorCount'));
  }

  const raw = (await getCachedFullConfig()) as unknown as RawKetchFullConfig;
  return raw.vendors?.length ?? 0;
}
